import {
  Button,
  LoadingOverlay,
  Modal,
  Notification,
  Select,
  Switch,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { IconCheck, IconX } from "@tabler/icons-react";
import {
  useCreateSocialMediaLinkMutation,
  useDeleteSocialMediaLinkMutation,
  useGetAllSocialMediaLinksQuery,
  useUpdateSocialMediaLinkMutation,
} from "Services/Admin/SocialMediaApiSlice";
import { SocialMediaLink } from "Types/Admin/SocialMediaLinks";
import React, { useEffect, useState } from "react";

interface SocialAccount {
  id: string;
  name: string;
  url: string;
  enabled: boolean;
  type:
    | "instagram"
    | "facebook"
    | "tiktok"
    | "twitter"
    | "linkedin"
    | "youtube";
}

const AccountsTab: React.FC = () => {
  const plant = process.env.REACT_APP_PLANT;

  const {
    data: socialMediaData,
    isLoading,
    refetch,
  } = useGetAllSocialMediaLinksQuery({ plant });

  const [createSocialMediaLink, { isLoading: isCreating }] =
    useCreateSocialMediaLinkMutation();
  const [updateSocialMediaLink, { isLoading: isUpdating }] =
    useUpdateSocialMediaLinkMutation();
  const [deleteSocialMediaLink, { isLoading: isDeleting }] =
    useDeleteSocialMediaLinkMutation();

  const [accounts, setAccounts] = useState<SocialAccount[]>([]);

  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const [addModalOpened, { open: openAddModal, close: closeAddModal }] =
    useDisclosure(false);
  const [editModalOpened, { open: openEditModal, close: closeEditModal }] =
    useDisclosure(false);
  const [editingAccount, setEditingAccount] = useState<SocialAccount | null>(
    null
  );

  useEffect(() => {
    if (socialMediaData?.results) {
      const apiResults = Array.isArray(socialMediaData.results)
        ? socialMediaData.results
        : [socialMediaData.results];

      const mappedAccounts: SocialAccount[] = apiResults.map(
        (item: SocialMediaLink) => ({
          id: item.id.toString(),
          name:
            item.accountType.charAt(0).toUpperCase() +
            item.accountType.slice(1),
          url: item.socialMediaUrl,
          enabled: item.showHide === 1,
          type: item.accountType as SocialAccount["type"],
        })
      );

      setAccounts(mappedAccounts);
    }
  }, [socialMediaData]);

  const addForm = useForm({
    initialValues: {
      url: "",
      enabled: true,
      type: "" as SocialAccount["type"],
    },
    validate: {
      url: (value: any) => {
        if (!value) return "URL is required";
        if (!value.startsWith("http://") && !value.startsWith("https://")) {
          return "URL must start with http:";
        }
        return null;
      },
      type: (value: any) => (!value ? "Account type is required" : null),
    },
  });

  const editForm = useForm({
    initialValues: {
      url: "",
      enabled: true,
      type: "" as SocialAccount["type"],
    },
    validate: {
      url: (value: any) => {
        if (!value) return "URL is required";
        if (!value.startsWith("http://") && !value.startsWith("https://")) {
          return "URL must start with http:";
        }
        return null;
      },
      type: (value: any) => (!value ? "Account type is required" : null),
    },
  });

  const handleAddAccount = async (values: typeof addForm.values) => {
    try {
      const createData = {
        accountType: values.type,
        plant: plant,
        showHide: values.enabled ? "1" : "0",
        socialMediaUrl: values.url,
      };

      await createSocialMediaLink(createData).unwrap();

      setNotificationMessage(
        `${
          values.type.charAt(0).toUpperCase() + values.type.slice(1)
        } account added successfully!`
      );
      setShowSuccessNotification(true);
      closeAddModal();
      addForm.reset();

      refetch();

      setTimeout(() => setShowSuccessNotification(false), 3000);
    } catch (error: any) {
      console.error("Failed to add account:", error);
      setNotificationMessage(
        error?.data?.message || "Failed to add account. Please try again."
      );
      setShowErrorNotification(true);
      setTimeout(() => setShowErrorNotification(false), 3000);
    }
  };

  const handleEditAccount = async (values: typeof editForm.values) => {
    if (!editingAccount) return;

    try {
      const updateData = {
        id: parseInt(editingAccount.id),
        plant: plant,
        showHide: values.enabled ? "1" : "0",
        socialMediaUrl: values.url,
      };

      await updateSocialMediaLink(updateData).unwrap();

      setNotificationMessage(
        `${editingAccount.name} account updated successfully!`
      );
      setShowSuccessNotification(true);
      closeEditModal();

      refetch();

      setTimeout(() => setShowSuccessNotification(false), 3000);
    } catch (error: any) {
      console.error("Failed to update account:", error);
      setNotificationMessage(
        error?.data?.message || "Failed to update account. Please try again."
      );
      setShowErrorNotification(true);
      setTimeout(() => setShowErrorNotification(false), 3000);
    }
  };

  const handleEditClick = (account: SocialAccount) => {
    setEditingAccount(account);
    editForm.setValues({
      url: account.url,
      enabled: account.enabled,
      type: account.type,
    });
    openEditModal();
  };

  const handleDeleteAccount = async (account: SocialAccount) => {
    try {
      await deleteSocialMediaLink({
        id: parseInt(account.id),
        plant,
      }).unwrap();

      setNotificationMessage(`${account.name} account deleted successfully!`);
      setShowSuccessNotification(true);
      setConfirmDeleteId(null);
      refetch();
      setTimeout(() => setShowSuccessNotification(false), 3000);
    } catch (error: any) {
      console.error("Failed to delete account:", error);
      setNotificationMessage(
        error?.data?.message || "Failed to delete account. Please try again."
      );
      setShowErrorNotification(true);
      setConfirmDeleteId(null);
      setTimeout(() => setShowErrorNotification(false), 3000);
    }
  };

  const getAccountIcon = (type: SocialAccount["type"]) => {
    switch (type) {
      case "facebook":
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="lucide lucide-facebook"
          >
            <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
          </svg>
        );
      case "instagram":
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="lucide lucide-instagram"
          >
            <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
            <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
            <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
          </svg>
        );
      case "twitter":
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="lucide lucide-twitter"
          >
            <path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z" />
          </svg>
        );
      case "linkedin":
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="lucide lucide-linkedin"
          >
            <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
            <rect width="4" height="12" x="2" y="9" />
            <circle cx="4" cy="4" r="2" />
          </svg>
        );
      case "tiktok":
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="lucide lucide-music"
          >
            <path d="M9 18V5l12-2v13" />
            <circle cx="6" cy="18" r="3" />
            <circle cx="18" cy="16" r="3" />
          </svg>
        );
      case "youtube":
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="lucide lucide-youtube"
          >
            <path d="M2.5 17a24.12 24.12 0 0 1 0-10 2 2 0 0 1 1.4-1.4 49.56 49.56 0 0 1 16.2 0A2 2 0 0 1 21.5 7a24.12 24.12 0 0 1 0 10 2 2 0 0 1-1.4 1.4 49.55 49.55 0 0 1-16.2 0A2 2 0 0 1 2.5 17" />
            <path d="m10 15 5-3-5-3z" />
          </svg>
        );
      default:
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="lucide lucide-globe"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="2" x2="22" y1="12" y2="12" />
            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
          </svg>
        );
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingOverlay visible={isLoading} />
      </div>
    );
  }

  return (
    <div className="font-body tracking-wider space-y-6 w-full overflow-x-hidden">

      {showSuccessNotification && (
        <div className="fixed top-4 right-2 left-2 md:right-4 md:left-auto z-50 w-auto md:w-96">

          <Notification
            icon={<IconCheck size="1.1rem" />}
            color="teal"
            title="Success"
            onClose={() => setShowSuccessNotification(false)}
          >
            {notificationMessage}
          </Notification>
        </div>
      )}

      {showErrorNotification && (
        <div className="fixed top-4 right-4 z-50 w-96">
          <Notification
            icon={<IconX size="1.1rem" />}
            color="red"
            title="Error"
            onClose={() => setShowErrorNotification(false)}
          >
            {notificationMessage}
          </Notification>
        </div>
      )}

      <div className="w-full flex flex-col lg:flex-row justify-between items-center gap-2">
        <div>
          <h2 className="text-sm font-medium text-center lg:text-start">
            Connected Social Media Accounts
          </h2>
          <p className="text-xs text-gray-500 mt-1 text-center lg:text-start">
            Manage your connected social media accounts
          </p>
        </div>
        <button
          onClick={openAddModal}
          className="px-4 py-2 text-sm text-white rounded-md bg-blue-600 hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={isCreating}
        >
          {isCreating ? "Adding..." : "Add Social Accounts"}
        </button>
      </div>

      <div className="space-y-4">
        {accounts.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No social media accounts found. Add your first account!
          </div>
        ) : (
          accounts.map((account) => (
            <div
              key={account.id}
                className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 py-4 border-t"
            >
              <div className="flex items-start lg:items-center gap-3">

                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                  {getAccountIcon(account.type)}
                </div>
                <div>
                  <h3 className="text-sm font-medium">{account.name}</h3>
                  <p
                    className={`text-xs ${
                      account.enabled ? "text-green-500" : "text-red-500"
                    }`}
                  >
                    {account.enabled ? "enabled" : "disabled"}
                  </p>
                  <p className="text-xs text-gray-500 break-all lg:truncate lg:max-w-xs max-w-full">

                    {account.url}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {confirmDeleteId === account.id ? (
                  <>
                    <span className="text-xs text-gray-600 mr-1">Delete?</span>
                    <button
                      onClick={() => handleDeleteAccount(account)}
                      className="px-3 py-1.5 border border-red-400 rounded-md text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 transition-colors disabled:opacity-50"
                      disabled={isDeleting}
                    >
                      {isDeleting ? "Deleting..." : "Yes"}
                    </button>
                    <button
                      onClick={() => setConfirmDeleteId(null)}
                      className="px-3 py-1.5 border border-gray-300 rounded-md text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors"
                      disabled={isDeleting}
                    >
                      No
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => handleEditClick(account)}
                      className="px-4 py-2 border border-gray-300 rounded-md text-xs font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={isUpdating || isDeleting}
                    >
                      {isUpdating ? "Updating..." : "Edit"}
                    </button>
                    <button
                      onClick={() => setConfirmDeleteId(account.id)}
                      className="px-4 py-2 border border-red-300 rounded-md text-xs font-medium text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={isDeleting || isUpdating}
                    >
                      Delete
                    </button>
                  </>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      <Modal
        opened={addModalOpened}
        onClose={closeAddModal}
        title="Add Social Account"
        centered
        size="sm"
      >
        <LoadingOverlay visible={isCreating} />
        <form
          onSubmit={addForm.onSubmit(handleAddAccount)}
          className="space-y-4"
        >
          <Select
            label="Account Type"
            placeholder="Select account type"
            data={[
              { value: "instagram", label: "Instagram" },
              { value: "facebook", label: "Facebook" },
              { value: "tiktok", label: "TikTok" },
              { value: "twitter", label: "Twitter" },
              { value: "linkedin", label: "LinkedIn" },
              { value: "youtube", label: "YouTube" },
            ]}
            required
            {...addForm.getInputProps("type")}
          />
          <TextInput
            label="Account URL"
            placeholder="https://www.instagram.com/username"
            required
            {...addForm.getInputProps("url")}
          />
          <Switch
            label="Enable account"
            checked={addForm.values.enabled}
            {...addForm.getInputProps("enabled", { type: "checkbox" })}
          />
          <div className="flex justify-end space-x-2 pt-4">
            <Button
              variant="outline"
              onClick={closeAddModal}
              disabled={isCreating}
            >
              Cancel
            </Button>
            <Button type="submit" color="blue" loading={isCreating}>
              Save
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        opened={editModalOpened}
        onClose={closeEditModal}
        title="Edit Social Account"
        centered
        size="sm"
      >
        <LoadingOverlay visible={isUpdating} />
        <form
          onSubmit={editForm.onSubmit(handleEditAccount)}
          className="space-y-4"
        >
          <Select
            label="Account Type"
            placeholder="Select account type"
            data={[
              { value: "instagram", label: "Instagram" },
              { value: "facebook", label: "Facebook" },
              { value: "tiktok", label: "TikTok" },
              { value: "twitter", label: "Twitter" },
              { value: "linkedin", label: "LinkedIn" },
              { value: "youtube", label: "YouTube" },
            ]}
            required
            {...editForm.getInputProps("type")}
            disabled
          />

          <TextInput
            label="Account URL"
            placeholder="https://www.instagram.com/username"
            required
            {...editForm.getInputProps("url")}
          />

          <Switch
            label="Enable account"
            checked={editForm.values.enabled}
            {...editForm.getInputProps("enabled", { type: "checkbox" })}
          />

          <div className="flex justify-end space-x-2 pt-4">
            <Button
              variant="outline"
              onClick={closeEditModal}
              disabled={isUpdating}
            >
              Cancel
            </Button>
            <Button type="submit" color="blue" loading={isUpdating}>
              Save
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default AccountsTab;
